import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axiosPublic from "../../api/axiosPublic";
import { resolveMetaGraphVersion } from "./metaGraphVersion";

export const FACEBOOK_SDK_TIMEOUT = 15000;
const FacebookSdkContext = createContext({ status: "loading", retry: () => {} });

export const FacebookProvider = ({ children }) => {
  const [status, setStatus] = useState("loading");
  const [revision, setRevision] = useState(0);
  const initialized = useRef(null);
  const retry = useCallback(() => { setStatus("loading"); setRevision(value => value + 1); }, []);

  useEffect(() => {
    let active = true;
    let script;
    let created = false;
    let version;
    const previousInit = window.fbAsyncInit;
    const fail = () => { if (active) { clearTimeout(timer); setStatus("failed"); } };
    const initialize = () => {
      if (!active || !version) return;
      if (typeof window.FB?.init !== "function") { fail(); return; }
      try {
        if (initialized.current !== version) {
          window.FB.init({
            appId: process.env.REACT_APP_CUSTOMER_META_APP_ID || process.env.REACT_APP_FB_APP_ID,
            cookie: true,
            xfbml: false,
            version,
          });
          initialized.current = version;
        }
        clearTimeout(timer);
        setStatus("ready");
      } catch { fail(); }
    };
    const timer = setTimeout(fail, FACEBOOK_SDK_TIMEOUT);
    setStatus("loading");
    window.fbAsyncInit = initialize;
    // Keep the browser SDK on the same version as API authorization and messaging.
    axiosPublic.get("/meta-auth/config", { timeout: 10000 }).then(({ data }) => {
      if (!active) return;
      version = resolveMetaGraphVersion(data?.graphApiVersion);
      if (typeof window.FB?.init === "function") initialize();
    }).catch(fail);
    if (typeof window.FB?.init === "function") {
      initialize();
    } else {
      script = document.getElementById("facebook-jssdk");
      if (revision > 0) { script?.remove(); script = null; }
      if (!script) {
        script = document.createElement("script");
        created = true;
      }
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.id = "facebook-jssdk";
      script.addEventListener("error", fail);
      script.addEventListener("load", initialize);
      if (created) document.body.appendChild(script);
    }
    return () => {
      active = false;
      clearTimeout(timer);
      script?.removeEventListener("error", fail);
      script?.removeEventListener("load", initialize);
      if (created) script.remove();
      if (window.fbAsyncInit === initialize) window.fbAsyncInit = previousInit;
    };
  }, [revision]);

  const value = useMemo(() => ({ status, retry, apiVersion: initialized.current || undefined }), [status, retry]);
  return (
    <FacebookSdkContext.Provider value={value}>
      {children}
    </FacebookSdkContext.Provider>
  );
};

export const useFacebookSdkState = () => useContext(FacebookSdkContext);
export const useFacebookSdk = () => useFacebookSdkState().status === "ready";
